import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {TIMER_ID_NO_ACTIVE} from '@/constants';
import {GlobalWindow} from '@/types';

interface CrossBrowserDocument extends Document {
  msHidden?: boolean;
  webkitHidden?: boolean;
  webkitVisibilityState?: unknown;
}

type VisibilityChangeCallback = (isVisible: boolean) => void;

const TIMER_DELAY = 100;

export class WindowVisibilityService {
  private document: CrossBrowserDocument;

  // ключ - передаваемый колбэк на событие visibility-change
  private callbacksMap: Map<
    VisibilityChangeCallback,
    {
      timerId: number;
      handler: VoidFunction;
    }
  >;

  constructor(private readonly globalWindow: GlobalWindow) {
    this.document = this.globalWindow.document as CrossBrowserDocument;
    this.callbacksMap = new Map();
  }

  public onVisibilityChange(handler: VisibilityChangeCallback): void {
    const checkFocusOrVisibility = (): void => {
      const timerId = this.globalWindow.setTimeout(() => handler(this.isFocusedOrDocumentVisible()), TIMER_DELAY);

      const cb = this.callbacksMap.get(handler);

      if (cb) {
        this.callbacksMap.set(handler, {
          timerId,
          handler: cb.handler,
        });
      }
    };

    this.callbacksMap.set(handler, {
      timerId: TIMER_ID_NO_ACTIVE,
      handler: checkFocusOrVisibility,
    });

    checkFocusOrVisibility();

    if (this.isSupportVisibilityApi()) {
      const visibilityChangeEventName = this.getBrowserVisibilityEventName();
      this.document.addEventListener(visibilityChangeEventName, checkFocusOrVisibility, false);
    } else {
      this.globalWindow.addEventListener('focus', checkFocusOrVisibility);
      this.globalWindow.addEventListener('blur', checkFocusOrVisibility);
    }
  }

  public offVisibilityChange(handler: VisibilityChangeCallback): void {
    const item = this.callbacksMap.get(handler);

    if (!item) return;

    this.globalWindow.clearTimeout(item.timerId);

    const cb = item.handler;

    if (this.isSupportVisibilityApi()) {
      const visibilityChangeEventName = this.getBrowserVisibilityEventName();
      this.document.removeEventListener(visibilityChangeEventName, cb, false);
    } else {
      this.globalWindow.removeEventListener('focus', cb);
      this.globalWindow.removeEventListener('blur', cb);
    }

    this.callbacksMap.delete(handler);
  }

  private getBrowserVisibilityEventName(): string {
    if (typeof this.document.hidden !== 'undefined') {
      // Opera 12.10 and Firefox 18 and later support
      return 'visibilitychange';
    } else if (typeof this.document.msHidden !== 'undefined') {
      return 'msvisibilitychange';
    } else if (typeof this.document.webkitHidden !== 'undefined') {
      return 'webkitvisibilitychange';
    }

    return 'visibilitychange';
  }

  private getBrowserDocumentHiddenProp(): string {
    if (typeof this.document.hidden !== 'undefined') {
      return 'hidden';
    } else if (typeof this.document.msHidden !== 'undefined') {
      return 'msHidden';
    } else if (typeof this.document.webkitHidden !== 'undefined') {
      return 'webkitHidden';
    }

    return '';
  }

  private isFocusedOrDocumentVisible(): boolean {
    return this.isSupportVisibilityApi() ? this.isDocumentVisible() : this.document.hasFocus();
  }

  private isDocumentVisible(): boolean {
    const visibilityElement = this.getBrowserDocumentHiddenProp() as keyof CrossBrowserDocument;

    return visibilityElement ? !this.document[visibilityElement] : true;
  }

  private isSupportVisibilityApi(): boolean {
    return !!(this.document.visibilityState || (this.document as CrossBrowserDocument).webkitVisibilityState);
  }
}

injected(WindowVisibilityService, DI_TOKENS.globalWindow);
